import { createContext, FC, PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';

import type { CollapsableGroupId, CollapsableTableExpandedByGroup } from './CollapsableTable.model';

type CollapsableTableContextValue = {
  expandedByGroup: CollapsableTableExpandedByGroup;
  isGroupExpanded: (groupId: CollapsableGroupId) => boolean;
  setGroupExpanded: (groupId: CollapsableGroupId, isExpanded: boolean) => void;
  toggleGroup: (groupId: CollapsableGroupId) => void;
};

const CollapsableTableContext = createContext<CollapsableTableContextValue | null>(null);

type CollapsableTableProviderProps = PropsWithChildren<{
  initialExpandedByGroup: CollapsableTableExpandedByGroup;
}>;

export const CollapsableTableProvider: FC<CollapsableTableProviderProps> = ({ children, initialExpandedByGroup }) => {
  const [expandedByGroup, setExpandedByGroup] = useState<CollapsableTableExpandedByGroup>(initialExpandedByGroup);

  useEffect(() => {
    setExpandedByGroup(previousState => {
      const nextState = { ...previousState };
      let hasNewGroup = false;

      Object.entries(initialExpandedByGroup).forEach(([groupId, isExpanded]) => {
        if (nextState[groupId] === undefined) {
          nextState[groupId] = isExpanded;
          hasNewGroup = true;
        }
      });

      return hasNewGroup ? nextState : previousState;
    });
  }, [initialExpandedByGroup]);

  const contextValue = useMemo<CollapsableTableContextValue>(
    () => ({
      expandedByGroup,
      isGroupExpanded: groupId => expandedByGroup[groupId],
      setGroupExpanded: (groupId, isExpanded) => {
        setExpandedByGroup(previousState => ({
          ...previousState,
          [groupId]: isExpanded,
        }));
      },
      toggleGroup: groupId => {
        setExpandedByGroup(previousState => ({
          ...previousState,
          [groupId]: !previousState[groupId],
        }));
      },
    }),
    [expandedByGroup],
  );

  return <CollapsableTableContext.Provider value={contextValue}>{children}</CollapsableTableContext.Provider>;
};

export const useCollapsableTableContext = (): CollapsableTableContextValue => {
  const context = useContext(CollapsableTableContext);

  if (context === null) {
    throw new Error('useCollapsableTableContext must be used within CollapsableTableProvider');
  }

  return context;
};
